import React, { useState, useEffect, useMemo } from 'react';
import { Page, Dashboard, ReflexFourHorizontalContainer } from '../../templates';
import { Content } from '../../organisms';
import  StyleColorImgFilter from './Filter';
import { StyleColorImgSub } from './StyleColorImgSub';
import { ImgUpload } from './ImgUpload';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { BackdropPanel, Label, FlexBoxVertical, FlexBoxHorizontal, Checkbox, Typography } from '../../atoms';

function StyleColorImg({ brdCd: brdCdProp }) {
  const [isLoading, setIsLoading] = useState(false);
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // 필터링된 데이터 상태 관리
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [colorData, setColorData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [orderData, setOrderData] = useState(null); // 발주 데이터를 위한 새로운 상태 추가
  const [noImageOnly, setNoImageOnly] = useState(false);
  const [styleTableData, setStyleTableData] = useState([]);
  
  // useMemo를 사용하여 브랜드 코드 계산 (props 우선, 없으면 URL에서 파싱)
  const brd_cd = useMemo(() => {
    if (brdCdProp) return brdCdProp;
    const pathname = window.location.pathname;
    const match = pathname.match(/\/interstellar\/([^\/]+)\/stylecolorimg/);
    return match ? match[1] : 'X';
  }, [brdCdProp]);

  useEffect(() => {
    setIsLoading(true);
    setFilterOptions(null);
    // setBrd_cd 호출 제거 - useMemo로 이미 계산됨
    // getBrandCode() 호출 제거 - brd_cd를 직접 사용
    fetch(`/api/style-color-img/${brd_cd}`)
      .then(response => response.json())
      .then(data => {
        setSnowflakeData(data);
        setFilterOptions({
          sesn: createOptions(data, 'sesn'),
          parentPrdtKind: createOptions(data, 'parent_prdt_kind_nm'),
          prdtKind: createOptions(data, 'prdt_kind_nm'),
          item: createOptions(data, 'item'),
          adultKid: createOptions(data, 'adult_kids_nm'),
        });
      })
      .catch(error => {
        console.error('Error fetching Snowflake data:', error);
      });
      setSelectedRow(null);
  }, [brd_cd]); // brd_cd를 dependency에 추가

  const handleColorData = (row) => {
    setColorData(row['color_all'].split(',').map(color => ({
      part_cd: row['part_cd'],
      color_cd: color,
      img: "https://static-dashff.fnf.co.kr/image/src/" + row['part_cd'] + "_" + color + ".jpg?timestamp=" + Date.now(),
      sesn: row['sesn']
    })));
  };

  const filterData = (options) => {
    const hasSearchValue = options.searchValue && options.searchValue.trim() !== '';
  
    // 비교 키 정의
    const comparisonKeys = {
      sesn: 'sesn',
      parentPrdtKind: 'parent_prdt_kind_nm',
      prdtKind: 'prdt_kind_nm',
      item: 'item',
      adultKid: 'adult_kids_nm',
    };
  
    // 필터링 로직
    const filteredData = snowflakeData.filter((item) => {
      const searchValueMatch = hasSearchValue
        ? ['part_cd', 'prdt_nm', 'color_all'].some((field) =>
            item[field]?.toString().toLowerCase().includes(options.searchValue.toLowerCase())
          )
        : true;

        const comparisonMatch = Object.keys(comparisonKeys).every((key) => {
          if (options[key].length === filterOptions[key].length) return true;
          return options[key].some(
            (option) => option.value.toUpperCase() === String(item[comparisonKeys[key]]).toUpperCase()
          );
        });
        
      return searchValueMatch && comparisonMatch;
    });
    setFilteredData(filteredData);
    // setTimeout(() => {setIsLoading(false)}, 500);
  };
  
  const handleImageSrcUpdate = (newImgSrc) => {
    setColorData((prevColorData) =>
      prevColorData.map((row) =>
        row.part_cd === selectedColor.part_cd && row.color_cd === selectedColor.color_cd
          ? { ...row, img: newImgSrc } // 해당 row의 img 값을 업데이트
          : row
      )
      );
    setSelectedRow((prevRow) =>
      prevRow.part_cd === selectedColor.part_cd
        ? { ...prevRow, img: updateImgTimestamp(prevRow.img) }
        : prevRow
    );
    setFilteredData((prevFilteredData) =>
      prevFilteredData.map((row) =>
        row.part_cd === selectedColor.part_cd
          ? { ...row, img: updateImgTimestamp(row.img) }
          : row
      )
    );
    };
    
  const handleRowClick = async (params) => {
    console.log(params.row);
    setSelectedRow(params.row);
    handleColorData(params.row);
    setSelectedColor({part_cd: params.row['part_cd'], color_cd: null, sesn: params.row['sesn'], brd_cd: brd_cd});
    
    // 발주 데이터 API 호출
    try {
      const response = await fetch(`/api/style-color-img/order/${brd_cd}/${params.row.sesn}/${params.row.part_cd}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      const orderData = await response.json();
      setOrderData(orderData);
    } catch (error) {
      console.error('Error fetching order data:', error);
      setOrderData(null);
    }
  };

  useEffect(() => {
    setIsLoading(true);
      const checkImages = async () => {
        // noImageOnly가 false면 이미지 체크 없이 바로 데이터 설정
        if (!noImageOnly) {
          setStyleTableData(filteredData);
          setIsLoading(false);
          return;
        }

        // noImageOnly가 true일 때만 이미지 체크 수행
        const itemsWithLoadStatus = await Promise.all(
          filteredData.map((item) =>
            new Promise((resolve) => {
              const img = new Image();
              
              img.onload = () => {
                resolve({ ...item, isLoaded: true });
              };
              
              img.onerror = () => {
                resolve({ ...item, isLoaded: false });
              };

              img.src = item.img;
            })
          )
        );
        
        setStyleTableData(itemsWithLoadStatus.filter(item => !item.isLoaded));
        setIsLoading(false);
      };
    
    checkImages();
  }, [noImageOnly, filteredData]);

  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      <Dashboard>
        <h1 style={{color:'#353339', margin: '10px'}}></h1>
        {filterOptions ? <StyleColorImgFilter data={filterOptions} onSearch={filterData} brd_cd={brd_cd} /> : <></>}
        <Content
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ReflexFourHorizontalContainer
            Left={    
              <FlexBoxVertical>
              <FlexBoxHorizontal style={{height: '40px', justifyContent: 'space-between', marginBottom: '7px'}}>
                <Label style={{color: '#353339', margin: '10px', width: '100px', alignSelf: 'baseline'}}>품번 선택</Label>
                {brd_cd === 'I' ? <></> :
                <div
                  style={{
                    width: '120px',
                  }}
                  onClick={() => {
                    setNoImageOnly(!noImageOnly)
                  }}
                >
                  <Checkbox
                    checked={noImageOnly}
                    setChecked={(value) => setNoImageOnly(value)}
                  />
                  <Typography
                    variant="content2"
                    style={{ cursor: 'pointer', marginLeft: '3px' }}
                  >
                    이미지 미등록
                  </Typography>
                </div>
                }
              </FlexBoxHorizontal>
                <DataGridPremium
                  sx={style}
                  columns={leftColumns}
                  rows={styleTableData}
                  getRowId={(row) => row['part_cd']}
                  hideFooter
                  rowHeight={40}
                  onRowClick={handleRowClick}
                  getRowClassName={(params) => {
                    return params.row.part_cd === selectedRow?.part_cd ? 'selected-row' : '';
                  }}
                  disableRowSelectionOnClick
                />
                </FlexBoxVertical>
            }
            LeftMiddle={
              <>
              <Label style={{color: '#353339', margin: '10px', width: '150px'}}>상세 정보 / 발주 현황</Label>
              <StyleColorImgSub 
                data={selectedRow} 
                orderData={orderData} 
                changeImg={handleImageSrcUpdate}
              />
            </>
            }
            RightMiddle={
              <FlexBoxVertical>
              <Label style={{color: '#353339', margin: '10px', width: '100px', alignSelf: 'baseline' }}>컬러 선택</Label>
              <DataGridPremium
                sx={style}
                columns={columns}
                rows={colorData}
                getRowId={(row) => row['color_cd']}
                onRowClick={(params) => {setSelectedColor(params.row);}}
                getRowClassName={(params) => {
                  return params.row.color_cd === selectedColor?.color_cd ? 'selected-row' : '';
                }}
                hideFooter
                rowHeight={160}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
              />
              </FlexBoxVertical>
            }
            Right={
              <>
                <Label style={{color: '#353339', margin: '10px', width: '100px'}}>이미지 업로드</Label>
                <ImgUpload data={selectedColor} changeImg={handleImageSrcUpdate}/>
              </>
            }
            ratio={{ left: 0.26, leftMiddle: 0.34, rightMiddle: 0.15}}
          />
        </Content>
      </Dashboard>
    </Page>
  );
}

export default StyleColorImg;


const columns = [
  { field: 'color_cd', headerName: '컬러', width: 100 },
  {
    field: 'img',
    headerName: '이미지',
    width: 200,
    align: 'left',
    renderCell: (props) =>
      (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}>
  <img
    src={props.value}
    onError={(e) => {
      e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
    }}
    style={{
      width: '80%',
      height: '80%',
      objectFit: 'contain',
      margin: '20px 0',
    }}
  />
</div>
      )
  },
  ];

  const leftColumns = [
    { field: 'part_cd', headerName: '품번', width: 120 },
    {
      field: 'img',
      headerName: '이미지',
      width: 90,
      renderCell: (props) =>
        (
          <div style={{ position: 'relative', width: '2vw', height: '3.5vh' }}>
    <img
      src={props.value}
      onError={(e) => {
        e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
      }}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        margin: '3px 0 3px 0',
      }}
    />
  </div>
        )
    },
    { field: 'prdt_nm', headerName: '제품명', width: 400 },
    
  ];
  

  const style = {
    height: '94%',
    width: '100%',
    fontSize: '0.8rem',
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: 'rgb(245, 245, 245) !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: 'white !important',
    },
    '& .MuiDataGrid-row.selected-row': {
      backgroundColor: 'lavender !important',
    },
    '& .MuiCheckbox-root': {
      color: '#666666',
      '&.Mui-checked': {
        color: '#666666',
      }
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer'
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none'
    }
  };
 
  const createOptions = (data, key, filterFn = (value) => value !== null) => {
    const options = Array.from(
      new Set(
        data.map((item) => (item[key] === null ? '-' : item[key])).filter(filterFn)
      )
    ).map((value) => ({
      value: key === 'life_style' ? value.toUpperCase() : value,
      label: key === 'life_style' ? value.toUpperCase() : value
    }));
  
    // 중복 제거 (distinct)
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.value, item])).values()
    );
  
    return distinctOptions.sort((a, b) => {
      // sesn : 숫자 내림차순, 영문 오름차순 정렬
      if (key === 'sesn') {
        const numA = parseInt(a.value, 10);
        const numB = parseInt(b.value, 10);
        const alphaA = a.value.slice(-2); // 'FW', 'SS' 등
        const alphaB = b.value.slice(-2);
  
        if (numA !== numB) {
          return numB - numA; // 숫자 내림차순
        }
        return alphaA.localeCompare(alphaB); // 영문 오름차순
      } else {
        // 기본 정렬 (예: '-'가 가장 먼저, 나머지는 알파벳 순)
        if (a.value === '-') return -1;
        if (b.value === '-') return 1;
        return a.value.localeCompare(b.value);
      }
    });
  };

  const CustomNoRowsOverlay = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        품번을 선택하세요.
      </div>
    );
  };

  const updateImgTimestamp = (imgUrl) => {
    const timestamp = Date.now();
    if (imgUrl.includes('?timestamp=')) {
      // 기존 timestamp를 새 값으로 교체
      return imgUrl.replace(/([?&]timestamp=)[0-9]+/, `$1${timestamp}`);
    } else {
      // timestamp가 없으면 추가
      return imgUrl + `?timestamp=${timestamp}`;
    }
  };

